import type { DeliveryAttempt, WithdrawalDetail, WithdrawalEvent } from './types';

const partner = Object.freeze({
  id: '00000000-0000-4000-8000-000000000101',
  code: 'PARTNER-A',
  name: 'Partner A',
});
const agent = Object.freeze({
  id: '00000000-0000-4000-8000-000000000201',
  code: 'AGENT-A',
  name: 'Agent A',
});

const common = {
  partner,
  walletId: '00000000-0000-4000-8000-000000001001',
  memberId: '00000000-0000-4000-8000-000000000301',
  initiatingAgent: agent,
  username: 'affiliate-a',
  partnerUserId: 'partner-user-a',
  amount: '1500.000000',
  currencyCode: 'THB',
  currencyMinorUnit: 2,
  reservedAt: '2026-07-21T08:00:00-04:00',
  completedAt: null,
  releasedAt: null,
  version: '5',
  createdBy: '00000000-0000-4000-8000-000000000001',
  updatedBy: '00000000-0000-4000-8000-000000000001',
} as const;

function detail(
  overrides: Partial<WithdrawalDetail> &
    Pick<
      WithdrawalDetail,
      'id' | 'status' | 'partnerWithdrawalId' | 'transferId' | 'createdAt' | 'updatedAt'
    >,
): WithdrawalDetail {
  return {
    ...common,
    partnerCreditId: null,
    failureCode: null,
    failureDetail: null,
    reservationAgeSeconds: null,
    attemptCount: '0',
    latestDelivery: null,
    requestMetadata: { receipt: '[MASKED]', authorization: '[MASKED]' },
    idempotency: {
      id: '00000000-0000-4000-8000-000000001501',
      key: '10000000-0000-4000-8000-000000000501',
      state: 'COMPLETED',
      responseStatus: 202,
      createdAt: overrides.createdAt,
      completedAt: overrides.updatedAt,
      expiresAt: '2026-07-24T12:00:00+07:00',
    },
    walletPostings: [
      {
        entryId: '00000000-0000-4000-8000-000000001102',
        entryType: 'WITHDRAWAL_RESERVE',
        availableDelta: '-1500.000000',
        reservedDelta: '1500.000000',
        withdrawnDelta: '0.000000',
        effectiveAt: '2026-07-21T08:00:00-04:00',
      },
    ],
    stateEventCount: '3',
    ...overrides,
  };
}

export const withdrawalFixtures = Object.freeze<readonly WithdrawalDetail[]>([
  detail({
    id: '00000000-0000-4000-8000-000000001201',
    status: 'CREDIT_STATUS_UNKNOWN',
    partnerWithdrawalId: 'partner-withdrawal-unknown',
    transferId: '00000000-0000-4000-8000-000000001601',
    partnerCreditId: 'partner-credit-secret-7890',
    failureCode: 'PARTNER_TIMEOUT',
    failureDetail: 'Partner status could not be confirmed',
    reservationAgeSeconds: '90061',
    attemptCount: '2',
    latestDelivery: {
      attemptId: '00000000-0000-4000-8000-000000001701',
      attemptNumber: 2,
      operation: 'STATUS_QUERY',
      normalizedResult: 'UNKNOWN',
      httpStatus: 504,
      retryable: true,
      partnerCreditId: 'partner-credit-secret-7890',
      errorCode: 'PARTNER_TIMEOUT',
      errorDetail: 'Timed out after a masked upstream request',
      startedAt: '2026-07-22T08:00:00-04:00',
      completedAt: '2026-07-22T08:00:30-04:00',
    },
    createdAt: '2026-07-21T08:00:00-04:00',
    updatedAt: '2026-07-23T08:00:30-04:00',
  }),
  detail({
    id: '00000000-0000-4000-8000-000000001202',
    status: 'MANUAL_REVIEW',
    partnerWithdrawalId: 'partner-withdrawal-review',
    transferId: '00000000-0000-4000-8000-000000001602',
    failureCode: 'INCONSISTENT_EVIDENCE',
    failureDetail: 'Masked evidence requires operator review',
    reservationAgeSeconds: '172800',
    attemptCount: '4',
    createdAt: '2026-07-20T10:00:00+07:00',
    updatedAt: '2026-07-22T10:00:00+07:00',
  }),
  detail({
    id: '00000000-0000-4000-8000-000000001203',
    status: 'COMPLETED',
    partnerWithdrawalId: 'partner-withdrawal-complete',
    transferId: '00000000-0000-4000-8000-000000001603',
    partnerCreditId: 'partner-credit-complete-4567',
    reservationAgeSeconds: null,
    attemptCount: '1',
    completedAt: '2026-07-19T11:01:00+07:00',
    createdAt: '2026-07-19T11:00:00+07:00',
    updatedAt: '2026-07-19T11:01:00+07:00',
  }),
]);

export const withdrawalEventFixtures = Object.freeze<readonly WithdrawalEvent[]>([
  {
    id: '00000000-0000-4000-8000-000000001801',
    withdrawalId: withdrawalFixtures[0]!.id,
    sequence: 1,
    previousStatus: null,
    newStatus: 'REQUESTED',
    reasonCode: null,
    reason: 'Withdrawal requested by partner',
    evidenceReference: null,
    approvalRequestId: null,
    actorId: '00000000-0000-4000-8000-000000000301',
    occurredAt: '2026-07-21T08:00:00-04:00',
  },
  {
    id: '00000000-0000-4000-8000-000000001802',
    withdrawalId: withdrawalFixtures[0]!.id,
    sequence: 2,
    previousStatus: 'CREDITING',
    newStatus: 'CREDIT_STATUS_UNKNOWN',
    reasonCode: 'PARTNER_TIMEOUT',
    reason: 'Partner result remains unknown',
    evidenceReference: 'case-42',
    approvalRequestId: null,
    actorId: '00000000-0000-4000-8000-000000000001',
    occurredAt: '2026-07-22T08:00:30-04:00',
  },
]);

export const withdrawalDeliveryAttemptFixtures = Object.freeze<readonly DeliveryAttempt[]>([
  {
    attemptId: '00000000-0000-4000-8000-000000001701',
    attemptNumber: 2,
    operation: 'STATUS_QUERY',
    normalizedResult: 'UNKNOWN',
    httpStatus: 504,
    retryable: true,
    partnerCreditId: 'partner-credit-secret-7890',
    errorCode: 'PARTNER_TIMEOUT',
    errorDetail: 'Timed out after a masked upstream request',
    startedAt: '2026-07-22T08:00:00-04:00',
    completedAt: '2026-07-22T08:00:30-04:00',
    withdrawalId: withdrawalFixtures[0]!.id,
    integrationVersionId: '00000000-0000-4000-8000-000000001901',
    transferId: withdrawalFixtures[0]!.transferId,
    endpointOrigin: 'https://credit.partner.example',
  },
  {
    attemptId: '00000000-0000-4000-8000-000000001702',
    attemptNumber: 1,
    operation: 'CREDIT',
    normalizedResult: 'RETRYABLE_FAILURE',
    httpStatus: 503,
    retryable: true,
    partnerCreditId: null,
    errorCode: 'PARTNER_UNAVAILABLE',
    errorDetail: 'Service unavailable',
    startedAt: '2026-07-21T08:01:00-04:00',
    completedAt: '2026-07-21T08:01:02-04:00',
    withdrawalId: withdrawalFixtures[0]!.id,
    integrationVersionId: '00000000-0000-4000-8000-000000001901',
    transferId: withdrawalFixtures[0]!.transferId,
    endpointOrigin: 'https://credit.partner.example',
  },
]);
